import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  FiHome, FiUsers, FiDollarSign,
  FiCheckSquare, FiFolder, FiBarChart2, FiSettings, FiLogOut,
  FiBell, FiChevronRight, FiMenu, FiChevronsLeft, FiFileText, FiCopy, FiX, FiHeadphones, FiMail,
} from 'react-icons/fi';
import { logout, usuarioLogado } from '../../services/auth';
import { buscarMinhasPermissoes } from '../../services/permissoes';
import { registrarAcao } from '../../services/auditoria';
import logo from '../../assets/logo.svg';
import styles from './Estrutura.module.css';
import useEsc from '../../hooks/useEsc';
import { resumoPendencias } from '../../services/onboarding';

import Dashboard from '../dashboard';
import Clientes from '../clientes';
import Leads from '../clientes/leads';
import Onboard from '../clientes/onboard';
import Procuracoes from '../clientes/procuracoes';
import Certificados from '../clientes/certificados';
import Licenciamento from '../clientes/licenciamento';
import IframeApp from '../sistema-externo';
import Configuracoes from '../configuracoes';
import Rotinas from '../rotinas';
import Tarefas from '../rotinas/Tarefas';
import CadastroRotinas from '../rotinas/CadastroRotinas';
import Nf from '../nf';
import NfClientes from '../nf/NfClientes';
import ConsultaNfs from '../nf/ConsultaNfs';
import ConsultaNfe from '../nf/ConsultaNfe';
import ConsultaCte from '../nf/ConsultaCte';
import ConsultaNfce from '../nf/ConsultaNfce';
import Pdf from '../pdf';
import Atendimento from '../atendimento';
import Email from '../email';
import Processos from '../processos';

function EmConstrucao({ modulo }) {
  return (
    <p style={{ color: '#8BA5C5', fontSize: 16, fontWeight: 500, margin: 0 }}>
      {modulo} em construção
    </p>
  );
}

/* Sistema de arquivamento (Docs — Arquivo Inteligente), hospedado à parte no
   Railway e aberto dentro do app num iframe que ocupa toda a área de
   conteúdo. O endereço é fixo aqui, com REACT_APP_ARQUIVOS_URL por cima pra
   poder apontar pra um ambiente de teste sem mexer no código -- diferente do
   Financeiro, que só existe via .env e sem ele cai no placeholder. */
const URL_ARQUIVOS = process.env.REACT_APP_ARQUIVOS_URL || 'https://docs.essensials.com.br';

/* Conteúdo de cada rota — a div wrapper em cada aba usa key={path}, então o
   React mantém a instância montada entre trocas de aba mesmo chamando de novo aqui */
function renderConteudoRota(path) {
  switch (path) {
    case '/app/dashboard':        return <Dashboard />;
    case '/app/clientes':         return <Clientes />;
    case '/app/clientes/leads':   return <Leads />;
    case '/app/clientes/onboard': return <Onboard />;
    case '/app/clientes/procuracoes': return <Procuracoes />;
    case '/app/clientes/certificados': return <Certificados />;
    case '/app/clientes/licenciamento': return <Licenciamento />;
    case '/app/clientes/acessos': return <EmConstrucao modulo="Acessos — em desenvolvimento" />;
    case '/app/financeiro':       return <IframeApp nome="Financeiro"  url={process.env.REACT_APP_FINANCEIRO_URL} />;
    case '/app/contratos':        return <EmConstrucao modulo="Contratos" />;
    case '/app/rotinas':          return <Rotinas />;
    case '/app/rotinas/tarefas':  return <Tarefas />;
    case '/app/rotinas/cadastro': return <CadastroRotinas />;
    case '/app/rotinas/processos': return <Processos />;
    case '/app/nf':               return <Nf />;
    case '/app/nf/clientes':      return <NfClientes />;
    case '/app/nf/consulta':      return <ConsultaNfs />;
    case '/app/nf/produtos':      return <ConsultaNfe />;
    case '/app/nf/cte':           return <ConsultaCte />;
    case '/app/nf/nfce':          return <ConsultaNfce />;
    case '/app/pdf':              return <Pdf />;
    case '/app/atendimento':      return <Atendimento />;
    case '/app/email':            return <Email />;
    case '/app/arquivos':         return <IframeApp nome="Docs" url={URL_ARQUIVOS} />;
    case '/app/relatorios':       return <EmConstrucao modulo="Relatórios" />;
    case '/app/configuracoes':    return <Configuracoes />;
    default:                      return null;
  }
}

const MENU_PRINCIPAL = [
  { icon: FiHome,          label: 'Dashboard',  path: '/app/dashboard',  permKey: 'dashboard' },
  {
    icon: FiUsers,
    label: 'Clientes',
    key: 'clientes',
    submenu: [
      { label: 'Leads',           path: '/app/clientes/leads',           permKey: 'clientes/leads' },
      { label: 'Onboard',         path: '/app/clientes/onboard',         permKey: 'clientes/onboard' },
      { label: 'Clientes',        path: '/app/clientes',                 permKey: 'clientes/clientes' },
      { label: 'Procurações',     path: '/app/clientes/procuracoes',     permKey: 'clientes/procuracoes' },
      { label: 'Certificados',    path: '/app/clientes/certificados',    permKey: 'clientes/certificados' },
      { label: 'Licenciamento',   path: '/app/clientes/licenciamento',   permKey: 'clientes/licenciamento' },
    ],
  },
  { icon: FiDollarSign,    label: 'Financeiro',  path: '/app/financeiro',  permKey: 'financeiro' },
  { icon: FiHeadphones,    label: 'Atendimento', path: '/app/atendimento', permKey: 'atendimento' },
  /* Obrigações abre direto o Painel; Tarefas, Rotinas e Processos viraram
     abas dele (31/10/2026). As chaves de permissão antigas continuam valendo
     -- quem tinha qualquer uma delas segue vendo o menu, e o Painel mostra
     só as abas permitidas. */
  { icon: FiCheckSquare,   label: 'Obrigações',  path: '/app/rotinas',     permKey: 'rotinas',
    permKeysAlt: ['rotinas/tarefas', 'rotinas/cadastro', 'rotinas/processos'] },
  { icon: FiMail,          label: 'E-mail',      path: '/app/email',       permKey: 'email' },
  {
    icon: FiFileText,
    label: 'NF',
    key: 'nf',
    submenu: [
      /* permKey segue 'nf' de propósito: é a mesma tela de sempre e quem
         já tinha permissão de NF continua entrando sem precisar remexer
         nas permissões salvas por usuário */
      { label: 'NF-Essensials', path: '/app/nf',          permKey: 'nf' },
      { label: 'NF-Clientes',   path: '/app/nf/clientes', permKey: 'nf/clientes' },
      { label: 'Consulta NFS',  path: '/app/nf/consulta', permKey: 'nf/consulta' },
      { label: 'Consulta NF Produtos', path: '/app/nf/produtos', permKey: 'nf/produtos' },
      { label: 'Consulta CT-e', path: '/app/nf/cte', permKey: 'nf/cte' },
      { label: 'Consulta NFC-e (SP)', path: '/app/nf/nfce', permKey: 'nf/nfce' },
    ],
  },
  { icon: FiCopy,          label: 'PDF',         path: '/app/pdf',         permKey: 'pdf' },
  /* rótulo é "Docs" (nome do sistema de arquivamento); a rota e a chave de
     permissão seguem 'arquivos' de propósito -- mudar quebraria as
     permissões já salvas por usuário */
  { icon: FiFolder,        label: 'Docs',        path: '/app/arquivos',    permKey: 'arquivos' },
  { icon: FiBarChart2,     label: 'Relatórios',  path: '/app/relatorios',  permKey: 'relatorios' },
];

const MENU_INFERIOR = [
  { icon: FiSettings,  label: 'Configurações', path: '/app/configuracoes', permKey: 'configuracoes' },
];

/* Registro path -> { label, icon }, usado para nomear as abas abertas */
const ROTA_REGISTRO = {};
[...MENU_PRINCIPAL, ...MENU_INFERIOR].forEach((item) => {
  if (item.submenu) {
    item.submenu.forEach((sub) => {
      ROTA_REGISTRO[sub.path] = { label: sub.label, icon: item.icon };
    });
  } else {
    ROTA_REGISTRO[item.path] = { label: item.label, icon: item.icon };
  }
});

/* ——— Item simples (NavLink) ——— */
function MenuItem({ icon: Icon, label, path }) {
  return (
    <li>
      <NavLink
        to={path}
        className={({ isActive }) =>
          `${styles.menuItem} ${isActive ? styles.active : ''}`
        }
      >
        <Icon className={styles.menuIcon} />
        <span className={styles.menuLabel}>{label}</span>
      </NavLink>
    </li>
  );
}

/* ——— Item expansível com submenu ——— */
function MenuItemExpandavel({ icon: Icon, label, itemKey, submenu, expandidos, onToggle }) {
  const expanded = expandidos.has(itemKey);

  return (
    <li>
      <button
        type="button"
        className={styles.menuItem}
        onClick={() => onToggle(itemKey)}
      >
        <Icon className={styles.menuIcon} />
        <span className={styles.menuLabel}>{label}</span>
        <FiChevronRight
          className={`${styles.seta} ${expanded ? styles.setaExpandida : ''}`}
        />
      </button>

      <div className={`${styles.submenu} ${expanded ? styles.submenuExpanded : ''}`}>
        {submenu.map((sub) => (
          <NavLink
            key={sub.path}
            to={sub.path}
            end
            className={({ isActive }) =>
              `${styles.submenuItem} ${isActive ? styles.submenuItemAtivo : ''}`
            }
          >
            <span className={styles.submenuLabel}>{sub.label}</span>
          </NavLink>
        ))}
      </div>
    </li>
  );
}

/* Aviso único ao entrar: clientes "Pendente" em Procurações, Certificados
   e Licenciamento (vindos do onboarding), com link pra cada lista. Uma vez
   por sessão do navegador. */
const MENUS_PENDENCIA = [
  ['procuracoes', 'Procurações', '/app/clientes/procuracoes'],
  ['certificados', 'Certificados', '/app/clientes/certificados'],
  ['licenciamento', 'Licenciamento', '/app/clientes/licenciamento'],
];
function AvisoPendencias({ resumo, onIr, onFechar }) {
  useEsc(onFechar);
  return (
    <div className={styles.avisoFundo}>
      <div className={styles.avisoCaixa}>
        <div className={styles.avisoTopo}>
          <h3>Cadastros pendentes</h3>
          <button type="button" onClick={onFechar} aria-label="Fechar">×</button>
        </div>
        <p>Clientes que vieram do onboarding e ainda precisam ser cadastrados:</p>
        {MENUS_PENDENCIA.filter(([k]) => resumo[k] > 0).map(([k, nome, path]) => (
          <button key={k} type="button" className={styles.avisoLinha} onClick={() => onIr(path)}>
            <span>{nome}</span><strong>{resumo[k]}</strong><em>abrir lista ›</em>
          </button>
        ))}
      </div>
    </div>
  );
}

function Estrutura() {
  const navigate = useNavigate();
  const location = useLocation();
  const usuario = usuarioLogado();
  const inicial = usuario?.nome?.charAt(0).toUpperCase() ?? 'U';

  const [sidebarAberta, setSidebarAberta] = useState(() =>
    localStorage.getItem('sidebar_aberta') !== 'false'
  );

  const [expandidos, setExpandidos] = useState(() => {
    const set = new Set();
    if (location.pathname.startsWith('/app/clientes')) set.add('clientes');
    if (location.pathname.startsWith('/app/rotinas')) set.add('obrigacoes');
    return set;
  });

  const [avisoPendencias, setAvisoPendencias] = useState(null);
  useEffect(() => {
    let jaAvisou = false;
    try { jaAvisou = sessionStorage.getItem('pendencias_avisadas') === '1'; } catch { /* sem sessionStorage: avisa */ }
    if (jaAvisou) return;
    resumoPendencias().then(r => {
      try { sessionStorage.setItem('pendencias_avisadas', '1'); } catch { /* idem */ }
      if (r?.total > 0) setAvisoPendencias(r);
    }).catch(() => {});
  }, []);

  /* Abas abertas — cada rota visitada vira uma aba que permanece montada */
  const [abas, setAbas] = useState([]);

  /* Sem sub-rota (ex: "/app") -> manda pro dashboard */
  useEffect(() => {
    if (location.pathname === '/app' || location.pathname === '/app/') {
      navigate('/app/dashboard', { replace: true });
    }
  }, [location.pathname, navigate]);

  /* endereços antigos de Tarefas/Rotinas/Processos (eram itens do menu
     Obrigações) abrem a aba correspondente do Painel */
  useEffect(() => {
    const destino = { '/app/rotinas/tarefas': 'tarefas_lista', '/app/rotinas/cadastro': 'cadastro', '/app/rotinas/processos': 'processos' }[location.pathname];
    if (destino) navigate(`/app/rotinas?aba=${destino}`, { replace: true });
  }, [location.pathname, navigate]);

  /* Toda rota visitada que exista no registro vira/permanece uma aba */
  useEffect(() => {
    const path = location.pathname;
    if (!ROTA_REGISTRO[path]) return;
    setAbas((prev) => (prev.includes(path) ? prev : [...prev, path]));
  }, [location.pathname]);

  function fecharAba(path, evento) {
    evento.stopPropagation();
    setAbas((prev) => {
      const idx = prev.indexOf(path);
      let restantes = prev.filter((p) => p !== path);

      if (path === location.pathname) {
        const proxima = restantes[idx] ?? restantes[idx - 1] ?? '/app/dashboard';
        if (!restantes.includes(proxima)) restantes = [...restantes, proxima];
        navigate(proxima);
      }

      return restantes;
    });
  }

  /* Permissões — lê cache do localStorage para evitar flash no carregamento */
  const [permissoes, setPermissoes] = useState(() => {
    try {
      const cached = localStorage.getItem('erp_perm');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });

  useEffect(() => {
    buscarMinhasPermissoes()
      .then(perm => {
        setPermissoes(perm);
        try { localStorage.setItem('erp_perm', JSON.stringify(perm)); } catch {}
      })
      .catch(() => {
        /* em caso de erro, usa admin=true apenas se não tem cache */
        setPermissoes(prev => prev ?? { admin: true, menus: [] });
      });
  }, []);

  /* Log de navegação — registra cada troca de página */
  useEffect(() => {
    const pagina = location.pathname.replace('/app/', '') || 'dashboard';
    registrarAcao('navegacao', 'Navegação', { page: pagina });
  }, [location.pathname]);

  /* Filtra menus com base nas permissões — null = ainda carregando, mostra vazio */
  const menuPrincipal = useMemo(() => {
    if (!permissoes) return [];
    if (permissoes.admin) return MENU_PRINCIPAL;
    const allowed = permissoes.menus ?? [];
    return MENU_PRINCIPAL
      .map(item => {
        if (item.submenu) {
          const subFiltrado = item.submenu.filter(s => allowed.includes(s.permKey));
          if (subFiltrado.length === 0) return null;
          return { ...item, submenu: subFiltrado };
        }
        return allowed.includes(item.permKey) || (item.permKeysAlt || []).some(k => allowed.includes(k)) ? item : null;
      })
      .filter(Boolean);
  }, [permissoes]);

  const menuInferior = useMemo(() => {
    if (!permissoes) return [];
    if (permissoes.admin) return MENU_INFERIOR;
    const allowed = permissoes.menus ?? [];
    return MENU_INFERIOR.filter(item => allowed.includes(item.permKey));
  }, [permissoes]);

  function toggleSidebar() {
    setSidebarAberta(v => {
      localStorage.setItem('sidebar_aberta', String(!v));
      return !v;
    });
  }

  function handleToggle(key) {
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleLogout() {
    try { localStorage.removeItem('erp_perm'); } catch {}
    logout();
    navigate('/');
  }

  return (
    <div className={styles.wrapper}>
      <aside className={`${styles.sidebar} ${sidebarAberta ? styles.sidebarAberta : ''}`}>
        <nav className={styles.nav}>
          {/* Botão toggle no canto superior */}
          <button
            className={styles.btnToggle}
            onClick={toggleSidebar}
            title={sidebarAberta ? 'Recolher menu' : 'Expandir menu'}
          >
            {sidebarAberta ? <FiChevronsLeft size={16} /> : <FiMenu size={16} />}
          </button>

          <div className={styles.navTop}>
            <ul className={styles.menuList}>
              {menuPrincipal.map((item) =>
                item.submenu ? (
                  <MenuItemExpandavel
                    key={item.key}
                    icon={item.icon}
                    label={item.label}
                    itemKey={item.key}
                    submenu={item.submenu}
                    expandidos={expandidos}
                    onToggle={handleToggle}
                  />
                ) : (
                  <MenuItem key={item.path} {...item} />
                )
              )}
            </ul>
          </div>

          <div className={styles.navBottom}>
            <div className={styles.separator} />
            <ul className={styles.menuList}>
              {menuInferior.map((item) => (
                <MenuItem key={item.path} {...item} />
              ))}
              <li>
                <button type="button" className={styles.menuItem} onClick={handleLogout}>
                  <FiLogOut className={styles.menuIcon} />
                  <span className={styles.menuLabel}>Sair</span>
                </button>
              </li>
            </ul>
          </div>
        </nav>
      </aside>

      <div className={styles.rightPanel}>
        <header className={styles.header}>
          <img src={logo} alt="Essensials" className={styles.headerLogo} />

          <div className={styles.abasContainer}>
            {abas.map((path) => {
              const info = ROTA_REGISTRO[path];
              const Icon = info?.icon;
              const ativa = path === location.pathname;
              return (
                <div
                  key={path}
                  className={`${styles.aba} ${ativa ? styles.abaAtiva : ''}`}
                  onClick={() => navigate(path)}
                  title={info?.label ?? path}
                >
                  {Icon && <Icon className={styles.abaIcon} />}
                  <span className={styles.abaLabel}>{info?.label ?? path}</span>
                  <button
                    type="button"
                    className={styles.abaFechar}
                    onClick={(e) => fecharAba(path, e)}
                    aria-label={`Fechar aba ${info?.label ?? ''}`}
                  >
                    <FiX />
                  </button>
                </div>
              );
            })}
          </div>

          <div className={styles.headerActions}>
            <button type="button" className={styles.iconButton} aria-label="Notificações">
              <FiBell />
            </button>
            <div className={styles.avatar} title={usuario?.nome}>{inicial}</div>
          </div>
        </header>

        {avisoPendencias && (
          <AvisoPendencias resumo={avisoPendencias} onFechar={() => setAvisoPendencias(null)}
            onIr={(path) => { setAvisoPendencias(null); navigate(path); }} />
        )}
        <main className={styles.main}>
          {abas.map((path) => (
            <div
              key={path}
              className={styles.abaConteudo}
              style={{ display: path === location.pathname ? 'block' : 'none' }}
            >
              {renderConteudoRota(path)}
            </div>
          ))}
        </main>
      </div>
    </div>
  );
}

export default Estrutura;
